/**
 * Export/Import System Constants
 *
 * Defines entity types, hydration modes, and other constants for the
 * YAML-based import/export system.
 */

/**
 * Supported entity types for export/import
 */
export const ENTITY_TYPES = {
  MODEL: 'model',
  TOOL: 'tool',
  KNOWLEDGE: 'knowledge',
  CONNECTOR: 'connector',
  IAM: 'iam',
  PROMPT: 'prompt',
  SKILL: 'skill',
  AGENT: 'agent',
} as const

export type EntityType = (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES]

/**
 * Entity type labels for display
 */
export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  model: 'Foundation Model',
  tool: 'Tool',
  knowledge: 'Knowledge Source',
  connector: 'Connector',
  iam: 'IAM Credential',
  prompt: 'Prompt',
  skill: 'Skill',
  agent: 'Agent',
}

/**
 * Hydration modes for import
 */
export const HYDRATION_MODES = {
  MATCH_OR_CREATE: 'match_or_create',
  MATCH_ONLY: 'match_only',
  CREATE_ONLY: 'create_only',
} as const

export type HydrationMode = (typeof HYDRATION_MODES)[keyof typeof HYDRATION_MODES]

/**
 * Hydration mode labels and descriptions
 */
export const HYDRATION_MODE_OPTIONS = [
  {
    value: HYDRATION_MODES.MATCH_OR_CREATE,
    label: 'Match or Create',
    description: 'Match existing entities by name/ID, create new if not found',
  },
  {
    value: HYDRATION_MODES.MATCH_ONLY,
    label: 'Match Only',
    description: 'Only match existing entities, skip if not found',
  },
  {
    value: HYDRATION_MODES.CREATE_ONLY,
    label: 'Create Only',
    description: 'Always create new entities, fail on name conflicts',
  },
] as const

/**
 * Import order for dependency resolution
 * Entities are imported in this order to satisfy dependencies
 */
export const IMPORT_ORDER: EntityType[] = [
  'iam', // No dependencies
  'model', // No dependencies
  'prompt', // No dependencies
  'connector', // Depends on IAM
  'knowledge', // Depends on Connectors, Models (for embedding)
  'tool', // Depends on IAM for auth
  'skill', // Depends on Tools, Models
  'agent', // Depends on all above
]

/**
 * Export format options
 */
export const EXPORT_FORMATS = {
  YAML: 'yaml',
  JSON: 'json',
} as const

export type ExportFormat = (typeof EXPORT_FORMATS)[keyof typeof EXPORT_FORMATS]

/**
 * File extensions for exports
 */
export const FILE_EXTENSIONS: Record<ExportFormat, string> = {
  yaml: '.yaml',
  json: '.json',
}

/**
 * MIME types for exports
 */
export const MIME_TYPES: Record<ExportFormat, string> = {
  yaml: 'text/yaml',
  json: 'application/json',
}

/**
 * Import result action types
 */
export const IMPORT_ACTIONS = {
  CREATED: 'created',
  MATCHED: 'matched',
  PLACEHOLDER: 'placeholder',
  SKIPPED: 'skipped',
  FAILED: 'failed',
} as const

export type ImportAction = (typeof IMPORT_ACTIONS)[keyof typeof IMPORT_ACTIONS]

/**
 * Import action labels and colors for display
 */
export const IMPORT_ACTION_CONFIG: Record<
  ImportAction,
  { label: string; color: string; icon: string }
> = {
  created: { label: 'Created', color: 'var(--success)', icon: 'plus-circle' },
  matched: { label: 'Matched', color: 'var(--info)', icon: 'check-circle' },
  placeholder: { label: 'Placeholder', color: 'var(--warning)', icon: 'alert-circle' },
  skipped: { label: 'Skipped', color: 'var(--text-muted)', icon: 'skip-forward' },
  failed: { label: 'Failed', color: 'var(--danger)', icon: 'x-circle' },
}

/**
 * Portable ID formats by entity type
 */
export const PORTABLE_ID_FORMATS: Record<EntityType, string> = {
  model: '{provider}:{model_id}',
  tool: '{display_name}',
  knowledge: '{display_name}',
  connector: '{connector_type}:{display_name}',
  iam: '{cloud_provider}:{display_name}',
  prompt: '{name}',
  skill: '{slug}',
  agent: '{name}',
}

/**
 * Maximum file size for import (10MB)
 */
export const MAX_IMPORT_FILE_SIZE = 10 * 1024 * 1024

/**
 * Rate limit for import operations (entities per minute)
 */
export const IMPORT_RATE_LIMIT = 100
